/*
 * Short Video Feed — client-side personalized vertical video feed with a
 * "Shop this look" slide-in product drawer.
 * - Catalog: inline <script data-svf-data> (Mode A) or fetch(data-catalog-url) (Mode B).
 * - Recommendation: tag-affinity in localStorage, GATED behind Shopify Customer Privacy.
 *   No consent => default order, zero tracking.
 * - DOM virtualization: only ~preload_window cards keep a <video> mounted.
 * No dependencies. Safe to load with `defer`.
 */
(function () {
  "use strict";

  var ICON = {
    heart: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    bag: '<svg viewBox="0 0 24 24"><path d="M6 7V6a6 6 0 0 1 12 0v1h3l-1 14H4L3 7h3zm2 0h8V6a4 4 0 0 0-8 0v1z"/></svg>',
    muteOn: '<svg viewBox="0 0 24 24"><path d="M3 10v4h4l5 5V5L7 10H3zm13.5 2a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4z"/></svg>',
    muteOff: '<svg viewBox="0 0 24 24"><path d="M3 10v4h4l5 5V5L7 10H3zm13.5 2a4.5 4.5 0 0 0-2.5-4v2.2l2.5 2.5V12zM4.3 3 3 4.3 7.7 9H3v6h4l5 5v-6.7l4.3 4.3 1.3-1.3L4.3 3z"/></svg>',
    play: '<svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>'
  };

  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  function canTrack() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.userCanBeTracked === "function") return !!cp.userCanBeTracked();
    } catch (e) {}
    return false; // default DENY — EU markets are live
  }
  function onConsent(cb) {
    if (window.Shopify && window.Shopify.customerPrivacy) cb();
    document.addEventListener("visitorConsentCollected", cb);
  }
  function money(amount, currency) {
    var n = parseFloat(amount);
    if (isNaN(n)) return "";
    try { return new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "USD" }).format(n); }
    catch (e) { return (currency ? currency + " " : "") + n.toFixed(2); }
  }

  function VideoFeed(root) {
    var self = this;
    this.root = root;
    this.viewport = root.querySelector("[data-svf-viewport]");
    this.fallbackHTML = this.viewport.innerHTML;
    this.drawer = root.querySelector("[data-svf-drawer]");
    this.drawerList = root.querySelector("[data-svf-drawer-list]");
    this.backdrop = root.querySelector("[data-svf-backdrop]");
    this.autoplay = root.dataset.autoplay !== "false";
    this.startMuted = root.dataset.muted !== "false";
    this.preload = Math.max(3, parseInt(root.dataset.preload, 10) || 5);
    this.exploration = Math.min(30, parseInt(root.dataset.exploration, 10) || 0);
    this.personalize = root.dataset.personalize !== "false";
    this.utm = (root.dataset.utm || "").trim();
    this.viewLabel = root.dataset.view || "View";
    this.catalogUrl = root.dataset.catalogUrl || "";
    var base = root.dataset.storageKey || "svf";
    this.base = base;
    this.AFF = base + ":aff";
    this.SEEN = base + ":seen";
    this.reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.muted = this.startMuted;
    try { if (sessionStorage.getItem(base + ":muted") === "0") this.muted = false; } catch (e) {}

    this.items = [];
    this.cards = [];
    this.dots = [];
    this.active = -1;

    this.bindDrawer();
    this.sizeViewport();
    this.bindResize();
    this.loadCatalog().then(function (items) {
      if (!Array.isArray(items) || !items.length) { self.empty(); return; }
      self.items = self.rank(items);
      self.applyDeepLink();
      self.build();
      root.dataset.svfReady = "true";
    }).catch(function (error) {
      self.empty();
      console.warn("Video feed could not start; direct video links remain available.", error);
    });
  }

  // The feed is the first section, so it sits below the theme header (announcement bar +
  // nav). A full 100svh card would push its bottom CTA off-screen. Size each card to the
  // height ACTUALLY visible below the header = innerHeight - section's document offset.
  // Detect a full-width fixed/sticky bar pinned to the BOTTOM of the viewport (e.g. the
  // theme's mobile tab bar Shop/Wishlist/Cart/...). Returns its height, else 0.
  VideoFeed.prototype.bottomChrome = function () {
    try {
      var cx = Math.floor(window.innerWidth / 2), H = window.innerHeight, h = 0, probes = [H - 2, H - 14];
      for (var i = 0; i < probes.length; i++) {
        var node = document.elementFromPoint(cx, probes[i]);
        while (node && node !== document.body && node !== document.documentElement) {
          var cs = getComputedStyle(node);
          if (cs.position === "fixed" || cs.position === "sticky") {
            var r = node.getBoundingClientRect();
            if (r.top >= H * 0.5 && r.bottom >= H - 3 && r.width >= window.innerWidth * 0.6) {
              h = Math.max(h, Math.round(H - r.top));
            }
            break;
          }
          node = node.parentElement;
        }
      }
      return Math.min(h, Math.round(H * 0.25));
    } catch (e) { return 0; }
  };

  // Detect small fixed corner widgets sitting above the nav bar (chat bubble, back-to-top)
  // and return how far to lift the overlay so the CTA/text clears them. Mobile only.
  // Shopify's chat draws its bubble inside nested shadow roots under a zero-size fixed
  // anchor, so descend into shadow roots and measure the corner-sized box on the way up.
  VideoFeed.prototype.cornerLift = function (bottomH) {
    try {
      var H = window.innerHeight, W = window.innerWidth, navTop = H - bottomH, gap = 12, lift = 0;
      var pts = [[30, navTop - 26], [W - 30, navTop - 26], [30, H - 96], [W - 30, H - 96]];
      for (var i = 0; i < pts.length; i++) {
        var x = pts[i][0], y = pts[i][1], node = document.elementFromPoint(x, y), box = null;
        for (var d = 0; node && node.shadowRoot && d < 10; d++) {
          var inner = node.shadowRoot.elementFromPoint(x, y);
          if (!inner || inner === node) break;
          node = inner;
        }
        while (node && node !== document.body && node !== document.documentElement) {
          var r = node.getBoundingClientRect();
          if (r.width > 0 && r.width < W * 0.45 && r.height < H * 0.35 && r.bottom > H * 0.6 && (!box || r.top < box.top)) box = r;
          var cs = getComputedStyle(node);
          if (cs.position === "fixed" || cs.position === "sticky") {
            if (box && r.width < W * 0.45) lift = Math.max(lift, Math.round(navTop - box.top + gap));
            break;
          }
          node = node.parentElement || (node.getRootNode && node.getRootNode().host) || null;
        }
      }
      return Math.max(0, Math.min(lift, Math.round(H * 0.3)));
    } catch (e) { return 0; }
  };

  VideoFeed.prototype.sizeViewport = function () {
    var docTop = this.root.getBoundingClientRect().top + (window.scrollY || window.pageYOffset || 0);
    var headerH = Math.max(0, Math.min(docTop, window.innerHeight * 0.4));
    if (this.root.dataset.family) {
      // Collection introductions aren't sticky browser chrome. Reserve only the site header.
      var siteHeader = document.querySelector(".t4s-section-header");
      headerH = siteHeader ? Math.max(0, Math.min(siteHeader.getBoundingClientRect().height, window.innerHeight * 0.25)) : 0;
    }
    var bottomH = this.bottomChrome();
    var avail = Math.max(320, Math.round(window.innerHeight - headerH - bottomH));
    this.headerH = Math.round(headerH);
    this.avail = avail;
    this.root.style.setProperty("--svf-h", avail + "px");
    this.root.style.setProperty("--svf-top", this.headerH + "px");
    var lift = (window.innerWidth < 1024) ? this.cornerLift(bottomH) : 0;
    if (lift > 0) this.root.style.setProperty("--svf-cta-bottom", lift + "px");
    else this.root.style.removeProperty("--svf-cta-bottom");
    // Undo any page-level snap from earlier builds — the inner container owns snapping now.
    var de = document.documentElement;
    de.style.scrollPaddingTop = "";
    de.style.scrollSnapType = "";
  };
  VideoFeed.prototype.bindResize = function () {
    var self = this, raf = 0;
    function recalc() {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () { self.sizeViewport(); self.refreshCaptions(); });
    }
    window.addEventListener("resize", recalc);
    window.addEventListener("orientationchange", recalc);
    window.addEventListener("load", recalc);
    // catch late layout shifts: announcement-bar close, web-font load, sticky header, chat bubble, etc.
    if (window.ResizeObserver) { try { new ResizeObserver(recalc).observe(document.body); } catch (e) {} }
    [300, 1200, 2500, 4500].forEach(function (t) { setTimeout(recalc, t); });
  };

  VideoFeed.prototype.loadCatalog = function () {
    var inline = this.root.querySelector("[data-svf-data]");
    if (inline) {
      try { return Promise.resolve(JSON.parse(inline.textContent)); }
      catch (e) { return Promise.resolve([]); }
    }
    if (this.catalogUrl) {
      return fetch(this.catalogUrl, { credentials: "omit" })
        .then(function (r) { return r.ok ? r.json() : []; })
        .catch(function () { return []; });
    }
    return Promise.resolve([]);
  };

  // ---- consent-gated storage ----
  VideoFeed.prototype.read = function (k) {
    if (!canTrack()) return {};
    try { return JSON.parse(localStorage.getItem(k) || "{}"); } catch (e) { return {}; }
  };
  VideoFeed.prototype.write = function (k, v) {
    if (!canTrack()) return;
    try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {}
  };
  VideoFeed.prototype.bump = function (tags, w) {
    if (!canTrack() || !tags || !tags.length) return;
    var a = this.read(this.AFF);
    for (var i = 0; i < tags.length; i++) a[tags[i]] = (a[tags[i]] || 0) + w;
    this.write(this.AFF, a);
  };
  VideoFeed.prototype.markSeen = function (id) {
    if (!canTrack()) return;
    var s = this.read(this.SEEN); s[id] = Date.now(); this.write(this.SEEN, s);
  };

  // ---- recommendation ----
  VideoFeed.prototype.rank = function (items) {
    if (!this.personalize || !canTrack()) return items.slice();
    var aff = this.read(this.AFF), seen = this.read(this.SEEN), now = Date.now();
    var expl = this.exploration / 100;
    return items.map(function (v, i) {
      var s = 0, t = v.tags || [];
      for (var j = 0; j < t.length; j++) s += aff[t[j]] || 0;
      var last = seen[v.id];
      if (last) { var hrs = (now - last) / 3.6e6; s -= Math.max(0, 1 - hrs / 24) * 5; }
      s += expl * 5 * Math.random();
      return { v: v, s: s, i: i };
    }).sort(function (a, b) { return b.s - a.s || a.i - b.i; }).map(function (o) { return o.v; });
  };

  VideoFeed.prototype.empty = function () {
    if (this.io) this.io.disconnect();
    if (this.pauseIO) this.pauseIO.disconnect();
    this.cards.forEach(function (card) { if (card.video) card.video.pause(); });
    this.viewport.innerHTML = this.fallbackHTML;
    delete this.root.dataset.svfReady;
  };

  // ---- deep-link: ?v=<id> brings that video card to the front (e.g. arriving from a
  // Pinterest pin). Matches catalog entry id (= source filename stem). No param => no-op,
  // so normal homepage visitors are unaffected. Overrides personalization for the first card.
  VideoFeed.prototype.applyDeepLink = function () {
    var id = null;
    try { id = new URLSearchParams(window.location.search).get("v"); } catch (e) {}
    if (!id) return;
    for (var i = 1; i < this.items.length; i++) {
      var it = this.items[i];
      if (it && (it.slug === id || it.id === id)) {
        this.items.unshift(this.items.splice(i, 1)[0]);
        return;
      }
    }
  };

  // Reflect the active video in the address bar (shareable, e.g. ?v=<slug>). Uses
  // history.replaceState so scrolling never spams the back stack. The landing URL stays
  // clean ("/") until the visitor scrolls — unless they arrived via a ?v= deep-link.
  VideoFeed.prototype.syncUrl = function (idx) {
    var c = this.cards[idx]; if (!c || !c.item) return;
    var anchor = c.item.slug || c.item.id;
    if (!anchor) return;
    try {
      var u = new URL(window.location.href);
      u.searchParams.set("v", anchor);
      window.history.replaceState(null, "", u.pathname + u.search + u.hash);
    } catch (e) {}
  };

  // ---- build ----
  VideoFeed.prototype.build = function () {
    var self = this;
    var frag = document.createDocumentFragment();
    var watchData = this.root.querySelector('[data-svf-watch-pages]');
    var watchPages = {};
    try { watchPages = JSON.parse(watchData ? watchData.textContent : '{}') || {}; } catch (e) {}

    this.items.forEach(function (item, idx) {
      var card = document.createElement("article");
      card.className = "svf__card is-paused";
      card.dataset.idx = idx;

      var media = document.createElement("div");
      media.className = "svf__media";
      if (item.poster) media.style.backgroundImage = 'url("' + item.poster + '")';
      card.appendChild(media);

      var shade = document.createElement("div"); shade.className = "svf__shade"; card.appendChild(shade);

      var hint = document.createElement("div");
      hint.className = "svf__muted-hint"; hint.innerHTML = ICON.play; card.appendChild(hint);

      var info = document.createElement("div"); info.className = "svf__info";
      if (item.title) { var h = document.createElement("p"); h.className = "svf__title"; h.textContent = item.title; info.appendChild(h); }
      var caption = null, captionToggle = null;
      if (item.caption) {
        caption = document.createElement("p");
        caption.className = "svf__caption";
        caption.id = self.root.id + "-caption-" + idx;
        caption.textContent = item.caption;
        info.appendChild(caption);
        captionToggle = document.createElement("button");
        captionToggle.type = "button";
        captionToggle.className = "svf__caption-toggle";
        captionToggle.hidden = true;
        captionToggle.setAttribute("aria-expanded", "false");
        captionToggle.setAttribute("aria-controls", caption.id);
        captionToggle.textContent = self.root.dataset.captionMore || "More";
        captionToggle.addEventListener("click", function (e) {
          e.stopPropagation();
          self.setCaptionExpanded(self.cards[idx], captionToggle.getAttribute("aria-expanded") !== "true");
        });
        info.appendChild(captionToggle);
      }
      if (item.page || item.slug) {
        var more = document.createElement("a");
        more.className = "svf__more"; more.href = self.pageUrl(watchPages[item.slug] || item.page || item.slug);
        more.textContent = self.root.dataset.fullLook || "See the full look →";
        more.addEventListener("click", function (e) { e.stopPropagation(); });
        info.appendChild(more);
      }
      if (item.products && item.products.length) {
        var cta = document.createElement("button");
        cta.type = "button"; cta.className = "svf__cta";
        cta.innerHTML = ICON.bag + "<span></span>";
        cta.querySelector("span").textContent = item.cta || "Shop this look";
        cta.addEventListener("click", function (e) { e.stopPropagation(); self.openDrawer(item); });
        info.appendChild(cta);
      }
      card.appendChild(info);

      var rail = document.createElement("div"); rail.className = "svf__rail";
      var like = document.createElement("button");
      like.type = "button"; like.className = "svf__btn"; like.setAttribute("aria-pressed", "false");
      like.setAttribute("aria-label", "Like"); like.innerHTML = ICON.heart;
      like.addEventListener("click", function (e) {
        e.stopPropagation();
        var on = like.getAttribute("aria-pressed") === "true";
        like.setAttribute("aria-pressed", on ? "false" : "true");
        if (!on) self.bump(item.tags, 3);
      });
      rail.appendChild(like);

      var mute = document.createElement("button");
      mute.type = "button"; mute.className = "svf__btn svf__mute";
      mute.setAttribute("aria-label", "Toggle sound");
      mute.innerHTML = self.muted ? ICON.muteOff : ICON.muteOn;
      mute.addEventListener("click", function (e) { e.stopPropagation(); self.toggleMute(); });
      rail.appendChild(mute);
      card.appendChild(rail);

      frag.appendChild(card);
      self.cards.push({ el: card, video: null, item: item, mounted: false, counted: false, info: info, caption: caption, captionToggle: captionToggle });
    });

    this.viewport.innerHTML = "";
    this.viewport.appendChild(frag);
    this.buildDots();
    this.refreshCaptions();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { self.refreshCaptions(); });

    // Active card detection within the inner scroller.
    this.io = new IntersectionObserver(function (entries) {
      var best = null;
      entries.forEach(function (e) {
        if (e.isIntersecting && e.intersectionRatio >= 0.6) {
          if (!best || e.intersectionRatio > best.intersectionRatio) best = e;
        }
      });
      if (best) self.setActive(+best.target.dataset.idx);
    }, { root: this.viewport, threshold: [0, 0.6, 1] });
    this.cards.forEach(function (c) { self.io.observe(c.el); });

    // Pause the active video when the whole feed is scrolled out of the page viewport.
    this.pauseIO = new IntersectionObserver(function (entries) {
      var e = entries[0]; if (!e) return;
      if (e.intersectionRatio < 0.5) { if (self.active >= 0) self.pause(self.active); }
      else if (self.autoplay && !self.reduced && self.active >= 0) self.play(self.active);
    }, { root: null, threshold: [0, 0.5, 1] });
    this.pauseIO.observe(this.viewport);

    this.bindKeys();
    this.bindHandoff();
    this.setActive(0);
  };

  // Smooth, decoupled hand-off between the feed (an inner scroll-snap container with
  // overscroll-behavior:contain, so it never tears into the page) and the sections below.
  // At the last card a further downward gesture eases the PAGE down to the next section;
  // at the top of that section an upward gesture eases back to the feed. One gesture =
  // one transition (cooldown), so the two scroll spaces never fight.
  VideoFeed.prototype.bindHandoff = function () {
    var self = this;
    function feedTop() { return self.root.getBoundingClientRect().top + window.scrollY - self.headerH; }
    function atFeed() { return window.scrollY < feedTop() + self.avail * 0.5; }
    function feedAtBottom() { var s = self.viewport; return s.scrollTop + s.clientHeight >= s.scrollHeight - 2; }
    function heroTarget() {
      var sec = self.root.closest(".shopify-section") || self.root;
      return Math.max(0, Math.round(sec.getBoundingClientRect().bottom + window.scrollY - self.headerH));
    }
    function ease(target) {
      if (self.transitioning) return;
      self.transitioning = true;
      window.scrollTo({ top: target, behavior: self.reduced ? "auto" : "smooth" });
      setTimeout(function () { self.transitioning = false; }, self.reduced ? 60 : 760);
    }
    window.addEventListener("wheel", function (e) {
      if (self.drawerOpen || e.target.closest(".svf__info.is-expanded")) return;
      if (self.transitioning) { e.preventDefault(); return; }
      if (atFeed()) {
        if (e.deltaY > 0 && feedAtBottom()) { e.preventDefault(); ease(heroTarget()); }
      } else if (e.deltaY < 0 && window.scrollY <= feedTop() + self.avail + 4) {
        e.preventDefault(); ease(Math.max(0, feedTop()));
      }
    }, { passive: false });
    var sy = 0;
    window.addEventListener("touchstart", function (e) { sy = e.touches[0].clientY; }, { passive: true });
    window.addEventListener("touchend", function (e) {
      if (self.drawerOpen || self.transitioning || e.target.closest(".svf__info.is-expanded")) return;
      var dy = sy - e.changedTouches[0].clientY; // >0 = swipe up
      if (atFeed() && feedAtBottom() && dy > 40) ease(heroTarget());
      else if (!atFeed() && window.scrollY <= feedTop() + self.avail + 4 && dy < -40) ease(Math.max(0, feedTop()));
    }, { passive: true });
  };

  VideoFeed.prototype.buildDots = function () {
    if (this.items.length < 2 || this.items.length > 12) return;
    var wrap = document.createElement("div"); wrap.className = "svf__dots";
    for (var i = 0; i < this.items.length; i++) {
      var d = document.createElement("span"); d.className = "svf__dot";
      wrap.appendChild(d); this.dots.push(d);
    }
    this.root.appendChild(wrap);
  };

  VideoFeed.prototype.productUrl = function (handle) {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
    var url = root + "products/" + handle;
    if (this.utm) url += (url.indexOf("?") === -1 ? "?" : "&") + this.utm;
    return url;
  };

  // Locale-aware URL for a direct path (e.g. "/collections/<handle>") with UTM.
  VideoFeed.prototype.absUrl = function (path) {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
    var url = path.charAt(0) === "/" ? root.replace(/\/$/, "") + path : root + path;
    if (this.utm) url += (url.indexOf("?") === -1 ? "?" : "&") + this.utm;
    return url;
  };

  // Locale-aware link to this video's own indexable page (/pages/<slug>).
  VideoFeed.prototype.pageUrl = function (slug) {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
    return root + "pages/" + slug;
  };

  VideoFeed.prototype.setActive = function (idx) {
    if (idx === this.active || idx < 0 || idx >= this.cards.length) return;
    var prev = this.active; this.active = idx;
    var half = Math.floor(this.preload / 2);
    var lo = Math.max(0, idx - half), hi = Math.min(this.cards.length - 1, idx + (this.preload - half - 1));
    for (var i = 0; i < this.cards.length; i++) {
      if (i >= lo && i <= hi) this.mount(i); else this.unmount(i);
    }
    if (prev >= 0 && this.cards[prev]) { this.pause(prev); this.setCaptionExpanded(this.cards[prev], false); this.cards[prev].el.classList.remove("is-active"); }
    this.cards[idx].el.classList.add("is-active");
    if (this.dots.length) {
      for (var d = 0; d < this.dots.length; d++) this.dots[d].classList.toggle("is-on", d === idx);
    }
    this.markSeen(this.cards[idx].item.id);
    if (this._urlStarted || /[?&]v=/.test(window.location.search)) this.syncUrl(idx);
    this._urlStarted = true;
    if (this.autoplay && !this.reduced) this.play(idx);
  };

  VideoFeed.prototype.mount = function (i) {
    var c = this.cards[i]; if (!c || c.mounted) return;
    var self = this, item = c.item;
    var v = document.createElement("video");
    v.className = "svf__media";
    v.muted = this.muted; v.loop = true; v.playsInline = true;
    v.setAttribute("playsinline", ""); v.setAttribute("webkit-playsinline", "");
    v.preload = "metadata";
    if (item.poster) v.poster = item.poster;
    v.src = item.src;
    v.addEventListener("click", function () { self.togglePlay(i); });
    v.addEventListener("timeupdate", function () {
      if (!c.counted && v.duration && v.currentTime / v.duration >= 0.6) {
        c.counted = true; self.bump(item.tags, 1);
      }
    });
    var posterDiv = c.el.querySelector("div.svf__media");
    if (posterDiv) c.el.replaceChild(v, posterDiv); else c.el.insertBefore(v, c.el.firstChild);
    c.video = v; c.mounted = true;
  };

  VideoFeed.prototype.unmount = function (i) {
    var c = this.cards[i]; if (!c || !c.mounted) return;
    if (c.video) {
      try { c.video.pause(); } catch (e) {}
      c.video.removeAttribute("src"); c.video.load();
      var posterDiv = document.createElement("div");
      posterDiv.className = "svf__media";
      if (c.item.poster) posterDiv.style.backgroundImage = 'url("' + c.item.poster + '")';
      c.el.replaceChild(posterDiv, c.video);
      c.video = null;
    }
    c.mounted = false; c.counted = false;
    c.el.classList.add("is-paused");
  };

  VideoFeed.prototype.play = function (i) {
    var c = this.cards[i]; if (!c || !c.video) return;
    c.video.muted = this.muted;
    var p = c.video.play();
    if (p && p.catch) p.catch(function () {});
    c.el.classList.remove("is-paused");
  };
  VideoFeed.prototype.pause = function (i) {
    var c = this.cards[i]; if (!c || !c.video) return;
    try { c.video.pause(); } catch (e) {}
    c.el.classList.add("is-paused");
  };
  VideoFeed.prototype.togglePlay = function (i) {
    var c = this.cards[i]; if (!c || !c.video) return;
    if (c.video.paused) this.play(i); else this.pause(i);
  };

  VideoFeed.prototype.toggleMute = function () {
    this.muted = !this.muted;
    try { sessionStorage.setItem(this.base + ":muted", this.muted ? "1" : "0"); } catch (e) {}
    this.cards.forEach(function (c) { if (c.video) c.video.muted = this.muted; }, this);
    var icon = this.muted ? ICON.muteOff : ICON.muteOn;
    this.root.querySelectorAll(".svf__mute").forEach(function (b) { b.innerHTML = icon; });
  };

  // ---- "Shop this look" drawer ----
  VideoFeed.prototype.bindDrawer = function () {
    var self = this;
    if (!this.drawer) return;
    var closeEls = this.root.querySelectorAll("[data-svf-drawer-close]");
    closeEls.forEach(function (b) { b.addEventListener("click", function () { self.closeDrawer(); }); });
    if (this.backdrop) this.backdrop.addEventListener("click", function () { self.closeDrawer(); });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && self.drawerOpen) self.closeDrawer();
    });
  };

  VideoFeed.prototype.openDrawer = function (item) {
    if (!this.drawer || !item.products || !item.products.length) return;
    var self = this;
    this.drawerList.innerHTML = "";
    item.products.forEach(function (p) {
      var a = document.createElement("a");
      a.className = "svf__product";
      a.href = p.url ? self.absUrl(p.url) : self.productUrl(p.handle);
      var img = document.createElement("img");
      img.src = p.image || ""; img.alt = p.title || ""; img.loading = "lazy";
      img.width = 64; img.height = 64;
      var info = document.createElement("div"); info.className = "svf__product-info";
      var name = document.createElement("p"); name.className = "svf__product-name"; name.textContent = p.title || p.handle;
      info.appendChild(name);
      var price = money(p.price, p.currency);
      if (price) { var pr = document.createElement("span"); pr.className = "svf__product-price"; pr.textContent = price; info.appendChild(pr); }
      var go = document.createElement("span"); go.className = "svf__product-go"; go.textContent = self.viewLabel;
      a.appendChild(img); a.appendChild(info); a.appendChild(go);
      a.addEventListener("click", function () { self.bump(item.tags, 2); self.markSeen(item.id); });
      self.drawerList.appendChild(a);
    });
    this.drawer.classList.add("is-open");
    this.drawer.setAttribute("aria-hidden", "false");
    if (this.backdrop) this.backdrop.classList.add("is-open");
    this.drawerOpen = true;
    var closeBtn = this.drawer.querySelector("[data-svf-drawer-close]");
    if (closeBtn) closeBtn.focus();
  };

  VideoFeed.prototype.closeDrawer = function () {
    if (!this.drawer) return;
    this.drawer.classList.remove("is-open");
    this.drawer.setAttribute("aria-hidden", "true");
    if (this.backdrop) this.backdrop.classList.remove("is-open");
    this.drawerOpen = false;
  };

  VideoFeed.prototype.setCaptionExpanded = function (card, expanded) {
    if (!card || !card.captionToggle) return;
    card.info.classList.toggle("is-expanded", expanded);
    card.captionToggle.setAttribute("aria-expanded", expanded ? "true" : "false");
    card.captionToggle.textContent = expanded ? (this.root.dataset.captionLess || "Less") : (this.root.dataset.captionMore || "More");
    if (expanded) card.caption.setAttribute("tabindex", "0");
    else { card.caption.removeAttribute("tabindex"); card.caption.scrollTop = 0; card.info.scrollTop = 0; }
  };

  VideoFeed.prototype.refreshCaptions = function () {
    (this.cards || []).forEach(function (card) {
      if (!card.caption || card.captionToggle.getAttribute("aria-expanded") === "true") return;
      card.captionToggle.hidden = card.caption.scrollHeight <= card.caption.clientHeight + 1;
    });
  };

  VideoFeed.prototype.bindKeys = function () {
    var self = this;
    this.viewport.addEventListener("keydown", function (e) {
      // Buttons keep native Enter/Space activation; scrolling caption text must not change films.
      if (e.target.closest("button, a, input, textarea, select, [contenteditable], .svf__info.is-expanded")) return;
      if (e.key === "ArrowDown" || e.key === "PageDown") { e.preventDefault(); self.go(self.active + 1); }
      else if (e.key === "ArrowUp" || e.key === "PageUp") { e.preventDefault(); self.go(self.active - 1); }
      else if (e.key === "m" || e.key === "M") { self.toggleMute(); }
      else if (e.key === " ") { e.preventDefault(); self.togglePlay(self.active); }
    });
  };
  VideoFeed.prototype.go = function (i) {
    if (i < 0 || i >= this.cards.length) return;
    this.cards[i].el.scrollIntoView({ behavior: this.reduced ? "auto" : "smooth", block: "start" });
  };

  ready(function () {
    var feeds = [].slice.call(document.querySelectorAll("[data-svf]"));
    feeds.forEach(function (root) {
      if (root.__svf) return; root.__svf = true;
      try { new VideoFeed(root); }
      catch (error) {
        console.warn("Video feed could not start; direct video links remain available.", error);
      }
    });
    onConsent(function () { /* affinity reads permitted for subsequent sessions */ });
  });
})();
